import { useCallback, useState } from "react";
import { addDays, format, parse } from "date-fns";
import useApiRequest from "../../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../../utils/hooks/useFacility";
import { GET_COURT_GRID_SCHEDULE_V2 } from "../../../../../api/apiRoutes";
import { CourtGridData } from "../../../../../models/CourtGridData";
import { Exercise } from "../../../../../models/Exercise";
import { SaleStep } from "../../../../../store/stepSaleConfig/stepSaleConfigReducer";
import { setTicketAccessFromExercise, TicketAccess } from "../../../../../models/TicketAccess";
import { KARNET_MAX_HORIZON_WEEKS } from "./karnetConcept";

export type KarnetTermStatus = "available" | "sold-out";

export interface KarnetTerm {
  index: number; // 1-based, 1 = wybrany (wyjściowy) termin
  date: string; // yyyy-MM-dd
  startTimeLabel: string; // HH:mm
  status: KarnetTermStatus;
  ticketAccess: TicketAccess;
}

interface UseKarnetScheduleProps {
  exerciseDetails: Exercise | null;
  saleStep?: SaleStep;
  anchorTicketAccess: TicketAccess;
}

/**
 * Wylicza kolejne terminy "co tydzień, ta sama godzina" pod istniejącym gridem grafiku.
 * Nie ma dedykowanego endpointu pod karnety — wykorzystujemy ten sam
 * GET_COURT_GRID_SCHEDULE_V2, którym posługuje się WeeklyGrid, tylko pytamy dalej w przyszłość.
 * Grafik poza wygenerowanym horyzontem po prostu nie zwraca danych dla danego dnia/godziny,
 * co traktujemy jako twardą granicę liczby możliwych powtórzeń.
 */
export default function useKarnetSchedule({ exerciseDetails, saleStep, anchorTicketAccess }: UseKarnetScheduleProps) {
  const { get } = useApiRequest();
  const facilityIds = useFacility();

  const [terms, setTerms] = useState<KarnetTerm[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const horizonCount = terms.length;
  const lastAvailableDateLabel = terms.length > 0 ? terms[terms.length - 1].date : "";

  const fetchHorizon = useCallback(
    async (requiredCapacity: number) => {
      if (!exerciseDetails) return;

      setLoading(true);

      const courtName = saleStep?.exerciseName?.split(";")[0].trim() || "";
      const anchorDate = new Date(exerciseDetails.startTime);
      const startTimeLabel = format(anchorDate, "HH:mm");

      const rangeFrom = format(addDays(anchorDate, 7), "yyyy-MM-dd");
      const rangeTo = format(addDays(anchorDate, 7 * (KARNET_MAX_HORIZON_WEEKS - 1)), "yyyy-MM-dd");

      const { result, isError } = await get(GET_COURT_GRID_SCHEDULE_V2(facilityIds, rangeFrom, rangeTo, courtName));

      const weeklyData: CourtGridData[] = !isError && Array.isArray(result) ? result : [];

      const computedTerms: KarnetTerm[] = [
        {
          index: 1,
          date: format(anchorDate, "yyyy-MM-dd"),
          startTimeLabel,
          status: "available",
          ticketAccess: anchorTicketAccess,
        },
      ];

      for (let week = 1; week < KARNET_MAX_HORIZON_WEEKS; week++) {
        const termDate = addDays(anchorDate, 7 * week);
        const dateLabel = format(termDate, "yyyy-MM-dd");

        const dayData = weeklyData.find((d) => d.date === dateLabel);
        const slot = dayData?.courts?.[0]?.timeSlots?.find((s) => s.startTime === startTimeLabel);

        // Brak slotu = grafik nie sięga tak daleko w przyszłość. Tu kończy się horyzont.
        if (!slot) break;

        const status: KarnetTermStatus = slot.capacity - slot.reserved >= requiredCapacity ? "available" : "sold-out";

        computedTerms.push({
          index: week + 1,
          date: dateLabel,
          startTimeLabel,
          status,
          ticketAccess: setTicketAccessFromExercise(
            slot.exerciseId,
            exerciseDetails.name,
            parse(`${dateLabel} ${startTimeLabel}`, "yyyy-MM-dd HH:mm", new Date()).getTime(),
          ),
        });
      }

      setTerms(computedTerms);
      setLoading(false);
      setHasFetched(true);
    },
    [exerciseDetails, saleStep, facilityIds, get, anchorTicketAccess],
  );

  const reset = useCallback(() => {
    setTerms([]);
    setHasFetched(false);
  }, []);

  return { terms, horizonCount, lastAvailableDateLabel, loading, hasFetched, fetchHorizon, reset };
}
